<template>
  <article class="user__profile_card">
    <header class="user__profile_header">
      <h1
        class="heading"
        v-if="heading"
      >
        {{ heading }}
      </h1>
      <a
        class="btn__action"
        v-if="myProfile.isModOrAdmin && !isCurrentUser"
        :href="modRoute"
      >{{ $t("buttons.TO_ADMIN") }}</a>
    </header>
    <section
      class="user__profile_info"
      id="my_profile"
    >
      <section id="user__profile_right">
        <img
          id="user__profile_picture"
          :src="user.picture"
          :style="`transform: rotate(${setImageRotation(user.rotation)}deg)`"
          :alt="$t('accessibility.ALT_PROFILE_PIC', { name: firstName })"
        >
        <button
          id="btn__edit_picture"
          class="btn__action"
          v-if="isCurrentUser"
          @click="changeProfilePicture = true"
          :disabled="disableEditing"
        >
          {{ $t("buttons.CHANGE_PROFILE_PICTURE") }}
        </button>
        <button
          id="btn__remove_picture"
          class="btn__action"
          v-if="isCurrentUser && !isDefaultPicture"
          @click="removeProfilePicture = true"
        >
          {{ $t("buttons.REMOVE_PROFILE_PICTURE") }}
        </button>
      </section>
      <section id="user__profile_left">
        <header class="user__profile_name">
          <h1 class="heading">
            {{ fullName }}
          </h1>
          <button
            id="btn__edit_description"
            class="btn__icon"
            v-if="isCurrentUser"
            @click="isEditing = !isEditing"
            :aria-label="$t('accessibility.ARIA_EXPAND_CLOSE')"
            :disabled="disableEditing"
          >
            <Icon name="edit" />
          </button>
        </header>
        <summary class="user__profile_description">
          <p class="copy large">
            {{ descriptionText }}
          </p>
        </summary>
        <template v-if="isEditing && isCurrentUser">
          <div class="input">
            <label
              class="label__input"
              :for="textareaId"
            >
              {{ $t("user.PROFILE_DESCRIPTION") }} {{ currentCharacterCount }}
            </label>
            <textarea
              :name="textareaId"
              :id="textareaId"
              cols="30"
              rows="10"
              maxlength="500"
              v-model="profileDescription"
            />
            <section class="form__controls">
              <button
                id="btn__cancel_edit"
                class="btn__neutral"
                @click="isEditing = false"
              >
                {{ $t("general.CANCEL") }}
              </button>
              <SpinnerButton
                :button-id="'btn__save_description'"
                :button-class="'btn__confirm'"
                :button-type="'submit'"
                :button-listener="onClickSubmitDescription"
                :button-text="$t('buttons.SAVE')"
              />
            </section>
          </div>
        </template>
        <summary class="user__profile_general">
          <p
            class="copy"
            v-html="profileInfo"
          />
        </summary>
        <summary
          class="user__profile_data"
          v-if="isCurrentUser"
        >
          <p class="copy small">
            {{ $t("user.MY_PROFILE_EMAIL", { email: myProfile.email }) }}
          </p>
          <p class="copy small">
            {{ $t("user.MY_PROFILE_USER_ID", { id: myProfile.userId }) }}
          </p>
        </summary>
      </section>
    </section>
    <modal
      v-if="showModal"
      @close="closeModal"
      :heading="modalData.heading"
      :subject="modalData.subject"
    >
      <template
        v-if="changeProfilePicture"
        v-slot:modal-actions
      >
        <Cropper :is-alias="alias" />
      </template>
      <template
        v-else
        v-slot:modal-buttons
      >
        <SpinnerButton
          :button-id="'btn__remove'"
          :button-class="'btn__warning'"
          :button-type="'submit'"
          :button-listener="onClickRemoveProfilePicture"
          :button-text="$t('buttons.REMOVE_PROFILE_PICTURE')"
        />
      </template>
    </modal>
  </article>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { formatDate, timeSince } from "@/helpers/functions";
import SpinnerButton from "../global/SpinnerButton";
import Icon from "../global/Icon.vue";
import Modal from "../global/Modal";
import Cropper from "./Cropper.vue";

export default {
  name: "UserProfileCard",
  components: {
    SpinnerButton,
    Icon,
    Modal,
    Cropper,
  },
  data() {
    return {
      isEditing: false,
      profileDescription: "",
      changeProfilePicture: false,
      removeProfilePicture: false,
    };
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    alias: {
      type: Boolean,
      required: true,
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
    heading: {
      type: String,
      required: false,
      default: "",
    },
    isCurrentUser: {
      type: Boolean,
      required: true,
    },
  },
  mounted() {
    this.profileDescription = this.user.description;
  },
  computed: {
    ...mapGetters(["myProfile"]),
    currentCharacterCount() {
      return `(${this.profileDescription.length}/500)`;
    },
    descriptionText() {
      if (!this.isCurrentUser && this.user.description) {
        return this.user.description;
      }
      if (!this.isCurrentUser && !this.user.description) {
        return this.$t("placeholders.NO_VISITED_DESCRIPTION");
      }
      if (this.isCurrentUser && this.user.description) {
        return this.user.description;
      }
      return this.$t("placeholders.NO_DESCRIPTION");
    },
    disableEditing() {
      if (this.isBlocked) return true;
      return false;
    },
    firstName() {
      if (this.alias) {
        return this.user.name;
      }
      return this.user.firstName;
    },
    fullName() {
      if (this.alias) return this.user.name;
      return `${this.user.firstName} ${this.user.lastName}`;
    },
    textareaId() {
      return this.alias ? "profile-description__alias" : "profile-description";
    },
    isDefaultPicture() {
      return this.user.picture.includes("profile-default");
    },
    modalData() {
      if (this.removeProfilePicture) {
        return {
          heading: this.$t("headings.REMOVE_PROFILE_PICTURE"),
          subject: this.$t("image.REMOVE_PROFILE_PICTURE_MODAL_TEXT"),
        };
      }
      return {
        heading: this.$t("headings.CHANGE_PROFILE_PICTURE"),
      };
    },
    modRoute() {
      const url = `${process.env.VUE_APP_MOD_ROUTE}/dashboard`;
      if (this.alias) return `${url}/alias/${this.user.slug}`;
      return `${url}/user/${this.user.slug}`;
    },
    profileInfo() {
      if (this.isCurrentUser) {
        return this.$t("user.MY_PROFILE_GENERAL", {
          name: this.firstName,
          date: formatDate(this.user.date),
          comments: this.user.comments,
          removed: this.user.removed,
        });
      }

      return this.$t("user.VISITED_PROFILE_GENERAL", {
        name: this.firstName,
        date: timeSince(this.user.date),
        comments: this.user.comments,
      });
    },
    showModal() {
      if (this.removeProfilePicture || this.changeProfilePicture) return true;
      return false;
    },
  },
  methods: {
    ...mapActions(["updateAlias", "updateProfile"]),
    closeModal() {
      this.changeProfilePicture = false;
      this.removeProfilePicture = false;
    },
    onClickSubmitDescription() {
      const payload = {
        id: this.user.id,
        description: this.profileDescription,
        profilePicture: this.user.picture,
        imageRotation: 0,
      };

      if (this.alias)
        this.updateAlias(payload).then(() => (this.isEditing = false));
      else this.updateProfile(payload).then(() => (this.isEditing = false));
    },
    onClickRemoveProfilePicture() {
      const payload = {
        id: this.user.id,
        description: this.profileDescription,
        profilePicture: process.env.VUE_APP_DEFAULT_PICTURE,
        imageRotation: 0,
      };

      if (this.alias) this.updateAlias(payload);
      else this.updateProfile(payload);
    },
    setImageRotation(number) {
      if (number == 1) return 0;
      return number;
    },
    formatDate,
  },
  watch: {
    user() {
      this.profileDescription = this.user.description;
    },
    isEditing(newVal) {
      if (!newVal) {
        this.profileDescription = this.user.description;
      }
    },
  },
};
</script>
